import type { ReactNode } from "react"

import { toast } from "sonner"
import { flushSync } from "react-dom"
import { openFile, buildLeadProfilePdfFileName } from "@/src/utils/url-utils"
import { useAuthContext } from "@/src/auth/hooks/use-auth-context"
import React, { useRef, useMemo, useState, useContext, useCallback, createContext } from "react"
import {
    enqueueLeadProfilePdf,
    fetchLeadProfilePdfUrl,
    refreshLeadProfilePdfUrl,
} from "@/src/actions/leadProfilePdf"

import LeadProfileDownloadDialog from "@/src/components/lead-profile-download-dialog/LeadProfileDownloadDialog"

const POLL_INTERVAL_MS = 3000
const MAX_POLL_MS = 60000
const MIN_DIALOG_VISIBLE_MS = 500

export type LeadProfileDownloadStatus = "idle" | "generatingProfile" | "downloadingProfile"

export type StartLeadProfileDownloadParams = {
    leadId: string
    profilePdfSignedUrl?: string
    onLeadRefresh?: () => Promise<unknown>
    candidateName?: string
}

type LeadProfileDownloadContextValue = {
    startLeadProfileDownload: (params: StartLeadProfileDownloadParams) => Promise<void>
    activeDownloadLeadId: string | null
    downloadStatus: LeadProfileDownloadStatus
}

const LeadProfileDownloadContext = createContext<LeadProfileDownloadContextValue | undefined>(undefined)

export const useLeadProfileDownloadContext = (): LeadProfileDownloadContextValue => {
    const contextValue = useContext(LeadProfileDownloadContext)
    if (!contextValue) {
        throw new Error("useLeadProfileDownloadContext must be used within a LeadProfileDownloadProvider")
    }
    return contextValue
}

const waitForMilliseconds = (milliseconds: number) =>
    new Promise<void>((resolve) => {
        setTimeout(resolve, milliseconds)
    })

const getElapsedMilliseconds = (startTime: number) => Date.now() - startTime

const waitForMinimumDialogVisibleTime = async (startTime: number) => {
    const remainingMilliseconds = MIN_DIALOG_VISIBLE_MS - getElapsedMilliseconds(startTime)
    if (remainingMilliseconds > 0) await waitForMilliseconds(remainingMilliseconds)
}

const canOpenSignedUrl = async (signedUrl: string): Promise<boolean> => {
    try {
        const response = await fetch(signedUrl)
        return response.ok
    } catch {
        return false
    }
}

const openSignedUrl = async (signedUrl: string, downloadFileName: string): Promise<boolean> => {
    const isUrlAccessible = await canOpenSignedUrl(signedUrl)
    if (!isUrlAccessible) return false
    await openFile(signedUrl, downloadFileName)
    return true
}

export const LeadProfileDownloadProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useAuthContext()
    const [downloadStatus, setDownloadStatus] = useState<LeadProfileDownloadStatus>("idle")
    const [activeDownloadLeadId, setActiveDownloadLeadId] = useState<string | null>(null)
    const isDownloadInProgressRef = useRef(false)

    const hasFileDownloadPermission = user?.permissions?.includes("fileDownload") ?? false

    const resolveSignedUrlFromLead = useCallback(
        async ({ leadId, onLeadRefresh }: StartLeadProfileDownloadParams): Promise<string | undefined> => {
            if (onLeadRefresh) {
                const refreshedLead = await onLeadRefresh()
                const leadData = (refreshedLead as { data?: { profilePdfSignedUrl?: string } } | undefined)?.data
                if (leadData?.profilePdfSignedUrl) return leadData.profilePdfSignedUrl
            }

            const fetchResult = await fetchLeadProfilePdfUrl(leadId)
            if (fetchResult.status === "error") {
                toast.error("Failed to fetch lead profile PDF")
                return undefined
            }
            return fetchResult.data
        },
        []
    )

    const pollForSignedUrl = useCallback(
        async (params: StartLeadProfileDownloadParams): Promise<string | undefined> => {
            const pollDeadline = Date.now() + MAX_POLL_MS

            const pollNextSignedUrl = async (): Promise<string | undefined> => {
                if (Date.now() >= pollDeadline) return undefined

                await waitForMilliseconds(POLL_INTERVAL_MS)
                const signedUrl = await resolveSignedUrlFromLead(params)
                if (signedUrl) return signedUrl

                return pollNextSignedUrl()
            }

            return pollNextSignedUrl()
        },
        [resolveSignedUrlFromLead]
    )

    const openWithRefreshFallback = useCallback(
        async (leadId: string, signedUrl: string, downloadFileName: string): Promise<boolean> => {
            const opened = await openSignedUrl(signedUrl, downloadFileName)
            if (opened) return true

            if (!hasFileDownloadPermission) {
                toast.error("Failed to download the file")
                return false
            }

            const refreshResult = await refreshLeadProfilePdfUrl(leadId)
            if (refreshResult.status === "error" || !refreshResult.data) {
                toast.error("Failed to download the file")
                return false
            }

            return openSignedUrl(refreshResult.data, downloadFileName)
        },
        [hasFileDownloadPermission]
    )

    const startLeadProfileDownload = useCallback(
        async (params: StartLeadProfileDownloadParams) => {
            if (isDownloadInProgressRef.current) return
            isDownloadInProgressRef.current = true
            const dialogOpenedAt = Date.now()
            flushSync(() => {
                setActiveDownloadLeadId(params.leadId)
                setDownloadStatus("generatingProfile")
            })

            try {
                let signedUrl = params.profilePdfSignedUrl

                if (!signedUrl) {
                    signedUrl = await resolveSignedUrlFromLead(params)
                }

                if (!signedUrl) {
                    const enqueueResult = await enqueueLeadProfilePdf(params.leadId)
                    if (enqueueResult.status === "error") {
                        toast.error("Failed to start profile PDF generation")
                        return
                    }

                    signedUrl = await pollForSignedUrl(params)
                    if (!signedUrl) {
                        toast.error("PDF is still generating, try again in a minute.")
                        return
                    }
                }

                setDownloadStatus("downloadingProfile")
                const downloadFileName = buildLeadProfilePdfFileName(params.candidateName)
                await openWithRefreshFallback(params.leadId, signedUrl, downloadFileName)
            } finally {
                await waitForMinimumDialogVisibleTime(dialogOpenedAt)
                isDownloadInProgressRef.current = false
                setDownloadStatus("idle")
                setActiveDownloadLeadId(null)
            }
        },
        [openWithRefreshFallback, pollForSignedUrl, resolveSignedUrlFromLead]
    )

    const contextValue = useMemo(
        () => ({ startLeadProfileDownload, activeDownloadLeadId, downloadStatus }),
        [startLeadProfileDownload, activeDownloadLeadId, downloadStatus]
    )

    return (
        <LeadProfileDownloadContext.Provider value={contextValue}>
            {children}

            <LeadProfileDownloadDialog open={activeDownloadLeadId !== null} downloadStatus={downloadStatus} />
        </LeadProfileDownloadContext.Provider>
    )
}
